import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from "@angular/core";
import { SQCCRecord } from "../../domain/upload-media-report-item";
import { UploadMediaReportRepository } from "../../domain/upload-media-report-repository";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { MediaInfo } from "../../domain/media";
import { MediaRepository } from "../../domain/media-repository";
import moment from "moment";
import { SchedulerAsideService } from "../scheduler/schedulerAside/scheduler-aside-service";
import { ToastrService } from "ngx-toastr";
import { UI } from "../../services/ui.service";
import { UserRepository } from "../../domain/user-repository";
import { User } from "../../domain/user";
import { TenantSettingsRepository } from "../../domain/tenant-settings-repository";
import { TenantSettings } from "../../domain/tenant-settings";

@Component({
  selector: "quality-check",
  templateUrl: "./quality-check.html",
  styleUrls: ["./quality-check.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QualityCheckComponent implements OnInit {
  mediaId: number;
  total: number;
  media: MediaInfo = null;
  placholderSnapshotImage = "/assets/icons/automated-qc-placeholder.svg";
  videoUrl = "";
  currentUser: User;
  tenantSettings: TenantSettings;
  qcPreviewSubtitleOffsetMs = 0;
  records: SQCCRecord[] = [];
  isLoading = false;
  selected: SQCCRecord[] = [];
  sorts = [
    {
      prop: "startTime",
      dir: "asc",
    },
  ];
  constructor(
    private uploadMediaReportRepository: UploadMediaReportRepository,
    private changeDetectorRef: ChangeDetectorRef,
    private route: ActivatedRoute,
    private mediaRepository: MediaRepository,
    private router: Router,
    private schedulerAsideService: SchedulerAsideService,
    private toastr: ToastrService,
    private ui: UI,
    private userRepository: UserRepository,
    private _tenantSettingsRepository: TenantSettingsRepository,
  ) {}

  async ngOnInit() {
    this.tenantSettings =
      await this._tenantSettingsRepository.getTenantSettings();
    this.currentUser = await this.userRepository.getCurrentUser();
    this.route.params.subscribe((params: Params) => {
      if (!!params["id"]) {
        this.mediaId = params["id"];
        this.schedulerAsideService.setCollapse(false);
        this.loadData().catch((err) => {
          console.error(err);
        });
      } else {
        this.router.navigate(["/scheduler/uploadReport"]);
      }
    });
  }

  async loadData() {
    this.isLoading = true;
    this.media = await this.mediaRepository.getMediaInfo(this.mediaId);
    if (!this.media) {
      return;
    }
    if (this.tenantSettings.UseMediaStartTimestampInScheduler) {
      this.qcPreviewSubtitleOffsetMs = this.media.startTimeStampMs;
    }
    this.videoUrl = await this.getPreviewUrl();
    this.records = await this.uploadMediaReportRepository.getMediaQCCReports(
      this.mediaId,
    );
    this.total = this.records.length;
    this.selected = this.records.filter((x) => x.approved);
    this.isLoading = false;
    this.changeDetectorRef.markForCheck();
  }

  async getPreviewUrl() {
    if (this.media.mediaLinkedType === 3) {
      return;
    }
    const previewUrl = await this.mediaRepository.getMediaPreviewUrl(
      this.mediaId,
    );
    return previewUrl || "";
  }

  formatMediaDuration(duration: moment.Duration) {
    return moment.utc(duration.asMilliseconds()).format("HH:mm:ss.SSS");
  }

  formatGeneralVolume(volume: number) {
    if (volume) {
      return volume.toFixed(2);
    }
    return "";
  }

  formatMediaType(mediaType) {
    const mediaTypes = {
      "0": "AudioVideo",
      "1": "Video",
      "2": "Audio",
      "3": "Image",
      "4": "FlexAnimation",
      "5": "Subtitles",
      "6": "DVBSubtitles",
      "7": "StreamIn",
      "8": "Unknown",
    };
    return mediaTypes[mediaType];
  }

  formatFrameFormat(frameFormat) {
    const frameFormats = {
      "0": "Unknown",
      "1": "Progressive",
      "2": "TopFieldFirst",
      "3": "BottomFieldFirst",
    };
    return frameFormats[frameFormat];
  }

  formatSeverity(severity) {
    switch (severity) {
      case 1:
        return "low";
      case 2:
        return "medium";
      case 3:
        return "high";
    }
  }

  formatDuration(value: moment.Duration) {
    return moment.utc(value.asMilliseconds()).format("HH:mm:ss.SSS");
  }

  onSelect(event) {
    if (this.currentUser.hasQcApprovalPermission) {
      this.approveSelected().catch((err) => {
        console.error(err);
      });
    }
  }

  onActivate(event) {
    if (
      event.type === "checkbox" &&
      this.currentUser.hasQcApprovalPermission === false
    ) {
      console.log("qc-activate", event);
      this.selected = this.records.filter((x) => x.approved);
      event.event.cancelBubble = true;
      event.event.returnValue = false;
      this.toastr.error("You don't have permission to perform this action");
      return false;
    }
  }
  approveAll() {
    if (this.currentUser.hasQcApprovalPermission) {
      if (!this.mediaId) {
        return;
      }
      if (this.selected.length != this.records.length) {
        this.selected = [...this.records];
      } else {
        this.selected = [];
      }

      this.onSelect(this.selected);
    } else {
      this.toastr.error("You don't have permission to perform this action");
    }
  }

  async approveSelected() {
    this.isLoading = true;

    await this.uploadMediaReportRepository.approveMediaQccRecords(
      this.mediaId,
      this.selected.map((x) => x.id),
    );
    this.toastr.info("Records updated");

    this.isLoading = false;
  }

  openPreview(event: SQCCRecord) {
    let startTime = event.startTime.clone();

    if (startTime.asSeconds() > 2) {
      startTime = startTime.subtract(
        moment
          .duration(2, "seconds")
          .add(moment().millisecond(), "milliseconds"),
      );
    }
    this.mediaRepository
      .getMediaPreviewUrl(this.mediaId)
      .then((signedTranscodedUrl) => {
        console.log("start time", startTime.asSeconds());
        this.ui.notify(this.ui.EVENTS.OpenVideoPreview, {
          mediaId: this.mediaId,
          snapshot: event.snapshot,
          autoPlay: true,
          startTime: startTime.asSeconds(),
          overridePreviewUrl: signedTranscodedUrl,
          showTimecode: true,
          framerate: this.media.contentFps,
          scale: 1.5,
        });
      });
    return false;
  }

  getCellClass({ row, column, value }) {
    // console.log('row',row);
    // console.log(this.selected);
    return { blured: this.selected.filter((x) => x.id == row.id).length > 0 };
  }

  TimeSortFunction(a: moment.Duration, b: moment.Duration): 1 | -1 | 0 {
    return a > b ? 1 : b > a ? -1 : 0;
  }
}
